import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { verifyCertificate } from '../services/hrApiService';

const HRVerifyPage = () => {
  const [verifyMethod, setVerifyMethod] = useState('file'); // 'file', 'cid', 'hash'
  const [file, setFile] = useState(null);
  const [cidInput, setCidInput] = useState('');
  const [hashInput, setHashInput] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState('');

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
    }
  };

  const handleVerify = async () => {
    setError('');
    setVerificationResult(null);
    setIsVerifying(true);

    try {
      let payload = {};
      
      if (verifyMethod === 'file') {
        if (!file) {
          setError('Please select a file to verify');
          setIsVerifying(false);
          return;
        }
        
        // Use service function
        const result = await verifyCertificate({ file });
        setVerificationResult(result);
        
      } else if (verifyMethod === 'cid') {
        if (!cidInput.trim()) {
          setError('Please enter a CID');
          setIsVerifying(false);
          return;
        }
        
        // Use service function
        const result = await verifyCertificate({ cid: cidInput.trim() });
        setVerificationResult(result);
        
      } else if (verifyMethod === 'hash') {
        if (!hashInput.trim()) {
          setError('Please enter a SHA256 hash');
          setIsVerifying(false);
          return;
        }
        
        // Use service function
        const result = await verifyCertificate({ hash: hashInput.trim() });
        setVerificationResult(result);
      }
      
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      VALID: 'bg-green-100 text-green-800 border-green-200',
      'NOT FOUND': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      SUSPICIOUS: 'bg-orange-100 text-orange-800 border-orange-200',
      FAKE: 'bg-red-100 text-red-800 border-red-200',
    };
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const handleDownload = () => {
    if (verificationResult?.ipfsUrl) {
      window.open(verificationResult.ipfsUrl, '_blank');
    }
  };

  const handleRequestReview = () => {
    alert('Review request submitted. Admin will be notified.');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-700 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link to="/hr-dashboard" className="inline-flex items-center text-white hover:text-gray-200 mb-4">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">Verify Certificate</h1>
          <p className="text-gray-200">Upload a file, paste a CID, or enter a SHA256 hash to verify authenticity</p>
        </div>

        {/* Verification Input Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-6">
          {/* Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Verification Method</label>
            <div className="flex gap-3">
              <button
                onClick={() => setVerifyMethod('file')}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  verifyMethod === 'file'
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Upload File
              </button>
              <button
                onClick={() => setVerifyMethod('cid')}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  verifyMethod === 'cid'
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Enter CID
              </button>
              <button
                onClick={() => setVerifyMethod('hash')}
                className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
                  verifyMethod === 'hash'
                    ? 'bg-orange-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Enter Hash
              </button>
            </div>
          </div>

          {/* File Upload */}
          {verifyMethod === 'file' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Certificate File
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-500 transition-colors">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-orange-500">
                      <span>Upload a file</span>
                      <input
                        id="file-upload"
                        type="file"
                        className="sr-only"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG up to 10MB</p>
                </div>
              </div>
              {file && (
                <div className="mt-3 flex items-center text-sm text-gray-700">
                  <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {file.name}
                </div>
              )}
            </div>
          )}

          {/* CID Input */}
          {verifyMethod === 'cid' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter IPFS CID
              </label>
              <input
                type="text"
                value={cidInput}
                onChange={(e) => setCidInput(e.target.value)}
                placeholder="QmXxxx..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-orange-600"
              />
            </div>
          )}

          {/* Hash Input */}
          {verifyMethod === 'hash' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter SHA256 Hash
              </label>
              <input
                type="text"
                value={hashInput}
                onChange={(e) => setHashInput(e.target.value)}
                placeholder="Enter 64-character SHA256 hash"
                className="w-full px-4 py-2 border border-gray-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-600 focus:border-orange-600"
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full bg-gradient-to-r from-orange-600 to-orange-500 text-white py-3 px-6 rounded-md font-semibold hover:from-orange-700 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md flex items-center justify-center"
          >
            {isVerifying ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Verifying...
              </>
            ) : (
              'Verify Certificate'
            )}
          </button>
        </div>

        {/* Verification Result */}
        {verificationResult && (
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Verification Result</h2>
              {getStatusBadge(verificationResult.status)}
            </div>

            {/* OCR Extracted Data Notice */}
            {verificationResult.ocrData && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-blue-800">OCR Analysis Complete</p>
                  <p className="text-xs text-blue-600 mt-0.5">Certificate data was automatically extracted using AI-powered OCR</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Student Name</label>
                <p className="text-gray-900 font-medium">{verificationResult.studentName || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Institution</label>
                <p className="text-gray-900 font-medium">{verificationResult.institution || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Roll Number</label>
                <p className="text-gray-900 font-medium">{verificationResult.rollNumber || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Certificate Type</label>
                <p className="text-gray-900 font-medium">{verificationResult.certificateType || 'N/A'}</p>
              </div>
            </div>

            {/* Additional OCR Details */}
            {verificationResult.ocrData && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  OCR Extracted Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {verificationResult.ocrData.issueDate && (
                    <div>
                      <span className="text-gray-500">Issue Date:</span>
                      <span className="ml-2 text-gray-900 font-medium">{verificationResult.ocrData.issueDate}</span>
                    </div>
                  )}
                  {verificationResult.ocrData.grade && (
                    <div>
                      <span className="text-gray-500">Grade:</span>
                      <span className="ml-2 text-gray-900 font-medium">{verificationResult.ocrData.grade}</span>
                    </div>
                  )}
                  {verificationResult.ocrData.program && (
                    <div>
                      <span className="text-gray-500">Program:</span>
                      <span className="ml-2 text-gray-900 font-medium">{verificationResult.ocrData.program}</span>
                    </div>
                  )}
                  {verificationResult.ocrData.department && (
                    <div>
                      <span className="text-gray-500">Department:</span>
                      <span className="ml-2 text-gray-900 font-medium">{verificationResult.ocrData.department}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-500 mb-1">File Hash (SHA256)</label>
              <p className="text-gray-900 font-mono text-sm break-all bg-gray-50 p-3 rounded">{verificationResult.fileHash || 'N/A'}</p>
            </div>

            {verificationResult.cid && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-500 mb-1">IPFS CID</label>
                <div className="flex items-center gap-2">
                  <p className="text-gray-900 font-mono text-sm bg-gray-50 p-3 rounded flex-1 break-all">{verificationResult.cid}</p>
                  <a
                    href={verificationResult.ipfsUrl || `https://gateway.pinata.cloud/ipfs/${verificationResult.cid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open
                  </a>
                </div>
              </div>
            )}

            {verificationResult.anchorUrl && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-500 mb-1">Blockchain Anchor</label>
                <a
                  href={verificationResult.anchorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  View on Blockchain Explorer
                </a>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              <button
                onClick={() => window.open(verificationResult.ipfsUrl || `https://gateway.pinata.cloud/ipfs/${verificationResult.cid}`, '_blank')}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                View on IPFS
              </button>
              <button
                onClick={handleDownload}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
              <button
                onClick={handleRequestReview}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Request Review
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HRVerifyPage;
